/**
 * LeatherRoundTool — the leather "Round" method (#974, epic #781): drag the
 * handle at a part's or cutout's corner to fillet it, drag it back past the
 * corner to make it sharp again.
 *
 * A thin shell, like `LeatherMirrorTool`: it OWNS a `LeatherRoundController`
 * and drives it by composition, supplying only the editor plumbing and pointer
 * wiring. The gesture, the handle overlay and the commit live in the
 * controller; the corner arithmetic and the handle-placement rules are shared
 * with the construction Round sub-tool.
 *
 * Mode-independent, like Mirror: corners belong to outlines, not to the
 * Leather / Cutout / Stitch mode you last drew in, so `app.ts` hides that row
 * while this is active. Seams are out of scope by design — a seam follows the
 * outline it came from, so rounding belongs to that outline.
 */
import type paper from "paper";
import { ToolBase } from "./ToolBase";
import type { PointerSample } from "../canvas/PointerInput";
import type { Scene, SceneSelection } from "../canvas/Scene";
import type { Project } from "../model/Project";
import type { ToolSettings } from "./EditorContext";
import { setCanvasCursor } from "../canvas/canvasCursor";
import {
  LeatherRoundController,
  type LeatherRoundHost,
} from "./leatherRoundController";

export class LeatherRoundTool extends ToolBase implements LeatherRoundHost {
  id = "leatherRound";
  label = "Round corners";
  // A plain pointer: the gesture grabs an existing handle rather than placing
  // anything, matching Mirror and the Offset kind.
  cursor = "default";

  private round = new LeatherRoundController(this);

  // --- LeatherRoundHost ----------------------------------------------------

  getScope(): paper.PaperScope {
    return this.ctx.scene.getPaperScope();
  }

  getScene(): Scene {
    return this.ctx.scene;
  }

  getProject(): Project {
    return this.ctx.history.state;
  }

  getSettings(): ToolSettings {
    return this.ctx.settings;
  }

  commit(label: string, mutate: (proj: Project) => Project): void {
    this.ctx.commit(label, mutate);
  }

  setSelection(sel: SceneSelection): void {
    this.ctx.setSelection(sel);
  }

  // --- Pointer flow --------------------------------------------------------

  onActivate(): void {
    setCanvasCursor(this.getScope(), this.cursor);
    this.round.refreshHandles();
  }

  onDeactivate(): void {
    this.round.clear();
  }

  onDown(s: PointerSample, p: { x: number; y: number }): void {
    this.round.handleDown(p, s);
  }

  onMove(s: PointerSample, p: { x: number; y: number }): void {
    this.round.handleMove(p, s);
  }

  onUp(): void {
    this.round.handleUp();
  }

  onCancel(): void {
    this.round.cancel();
  }

  /** Redraw the corner dots — called when the project or selection changes
   *  under the tool, so the handles track the geometry they belong to. */
  refreshHandles(): void {
    this.round.refreshHandles();
  }

  /** The selection changed (canvas or Parts list): follow it, and apply an
   *  already-set radius to the newly-picked object. */
  onSelectionChanged(): void {
    this.round.onSelectionChanged();
  }

  /** Panel preset: the radius a bare click on a corner applies. */
  setPresetRadius(mm: number | null): void {
    this.round.setPresetRadius(mm);
  }

  /** Per-frame drag radius, for the panel's Radius readout. */
  setLiveRadiusCallback(fn: ((mm: number) => void) | undefined): void {
    this.round.setLiveRadiusCallback(fn);
  }

  /** Test hook (#974): the corner being dragged, or null. */
  getDragCorner(): string | null {
    return this.round.getHoverCorner();
  }

  /** Test hook (#974): where every corner handle sits, in project mm. */
  getHandlePositions() {
    return this.round.getHandlePositions();
  }
}
